/** X1 contracts contain no admissions thresholds. Policies are entered and approved by administrators. */
export const eligibilityDisclaimer =
  "Preliminary guidance and does not guarantee admission, as the institution retains the final admissions decision.";
export const eligibilityCategories = [
  "academic",
  "english",
  "experience",
  "programme",
  "intake",
] as const;
export type EligibilityCategory = (typeof eligibilityCategories)[number];
export type EligibilityOutcome =
  | "CRITERIA_MET"
  | "CRITERIA_NOT_MET"
  | "REVIEW_REQUIRED";
export type EligibilityInputs = Partial<
  Record<EligibilityInputField, string | number>
>;
export const eligibilityFields = [
  {
    field: "qualification",
    label: "Academic qualification",
    category: "academic",
    type: "text",
  },
  {
    field: "qualificationCountry",
    label: "Qualification country",
    category: "academic",
    type: "text",
  },
  {
    field: "studyField",
    label: "Subject / field of study",
    category: "academic",
    type: "text",
  },
  {
    field: "gradingScale",
    label: "Original grading scale",
    category: "academic",
    type: "text",
  },
  {
    field: "grade",
    label: "Grade / GPA on that scale",
    category: "academic",
    type: "number",
  },
  {
    field: "englishTest",
    label: "English test and version",
    category: "english",
    type: "text",
  },
  {
    field: "englishDate",
    label: "English test date",
    category: "english",
    type: "date",
  },
  {
    field: "englishOverall",
    label: "Overall score",
    category: "english",
    type: "number",
  },
  {
    field: "englishListening",
    label: "Listening score",
    category: "english",
    type: "number",
  },
  {
    field: "englishReading",
    label: "Reading score",
    category: "english",
    type: "number",
  },
  {
    field: "englishWriting",
    label: "Writing score",
    category: "english",
    type: "number",
  },
  {
    field: "englishSpeaking",
    label: "Speaking score",
    category: "english",
    type: "number",
  },
  {
    field: "gapMonths",
    label: "Study gap (months)",
    category: "experience",
    type: "integer",
  },
  {
    field: "workMonths",
    label: "Work experience (months)",
    category: "experience",
    type: "integer",
  },
  {
    field: "programmeNotes",
    label: "Programme-specific information",
    category: "programme",
    type: "textarea",
  },
] as const;
export type EligibilityInputField = (typeof eligibilityFields)[number]["field"];
export interface EligibilityRule {
  id: string;
  label: string;
  category: EligibilityCategory;
  field: EligibilityInputField | "intake";
  operator: "min" | "max" | "equals" | "oneOf" | "manual" | "notApplicable";
  value?: number | string | string[];
  required: boolean;
  group?: string;
}
export interface EligibilityPolicyBody {
  intake: string;
  sourceTitle: string;
  sourceReference: string;
  effectiveFrom: string;
  effectiveUntil: string;
  coverage: Record<EligibilityCategory, "rules" | "notApplicable">;
  rules: EligibilityRule[];
}
export interface EligibilityCriterionResult {
  id: string;
  label: string;
  category: EligibilityCategory;
  state: "MET" | "NOT_MET" | "UNKNOWN" | "ADVISORY";
  reason: string;
}
export const outcomeLabel: Record<EligibilityOutcome, string> = {
  CRITERIA_MET: "Preliminary criteria met",
  CRITERIA_NOT_MET: "Criteria not met",
  REVIEW_REQUIRED: "Review required",
};
/** Empty is unknown. Zero is a valid entered value; never coerce absent fields into zero. */
export function parseEligibilityInputs(
  values: Record<string, string>,
): EligibilityInputs {
  const result: EligibilityInputs = {};
  for (const spec of eligibilityFields) {
    const value = (values[spec.field] || "").trim();
    if (!value) continue;
    if (spec.type === "number" || spec.type === "integer") {
      if (!/^\d+(?:\.\d+)?$/.test(value))
        throw new Error(`Enter a valid ${spec.label.toLowerCase()}.`);
      const number = Number(value);
      if (
        !Number.isFinite(number) ||
        number > 100000 ||
        (spec.type === "integer" && !Number.isInteger(number))
      )
        throw new Error(`Enter a valid ${spec.label.toLowerCase()}.`);
      result[spec.field] = number;
    } else {
      if (value.length > (spec.type === "textarea" ? 2000 : 200))
        throw new Error(`${spec.label} is too long.`);
      if (
        spec.type === "date" &&
        (!/^\d{4}-\d{2}-\d{2}$/.test(value) ||
          !Number.isFinite(Date.parse(value)) ||
          new Date(value).toISOString().slice(0, 10) !== value)
      )
        throw new Error("Enter a valid English test date.");
      result[spec.field] = value;
    }
  }
  return result;
}
export function inputValues(inputs: EligibilityInputs): Record<string, string> {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => [key, String(value)]),
  );
}
